<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="noMarginCard">
          <v-card-title class="justify-space-between">
            <div class="d-flex align-center">
              <span>Playwright Test Runner</span>
              <v-btn class="ml-3" small color="secondary" :loading="running" @click="runFullTargetMatrix">
                <v-icon left>mdi-playlist-play</v-icon>Full test
              </v-btn>
            </div>
            <v-btn small color="secondary" :loading="loadingFolders" @click="loadPlaywrightFolders">
              <v-icon left>mdi-refresh</v-icon>Aggiorna target
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert type="info" dense text>
              Esegui l'intera suite Playwright oppure solo sottocartelle in
              <strong>playwright-tests/playbooks</strong>.
            </v-alert>

            <v-row>
              <v-col cols="12" md="5">
                <v-select
                  v-model="selectedTarget"
                  :items="playwrightTargets"
                  dense
                  color="primary"
                  label="Target"
                  hint="Scegli il portale su cui lanciare i test"
                  persistent-hint
                  :disabled="running"
                />
              </v-col>
              <v-col cols="12" md="5">
                <v-switch
                  class="playwright-flow-switch"
                  :class="{ 'ui-active': selectedFlow === 'ui' }"
                  v-model="selectedFlow"
                  :true-value="'ui'"
                  :false-value="'api'"
                  :prepend-icon="selectedFlow === 'ui' ? 'mdi-google-chrome' : 'mdi-language-javascript'"
                  :label="selectedFlow === 'ui' ? 'UI' : 'API'"
                  inset
                  dense
                  :color="selectedFlow === 'ui' ? 'primary' : 'success'"
                  :track-color="selectedFlow === 'ui' ? 'primary' : 'success'"
                  hint="Attiva UI (Chrome) o disattiva per API (JavaScript)"
                  persistent-hint
                  :disabled="running"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-btn block color="primary" :loading="running" @click="runFullSuite">
                  <v-icon left>mdi-play-circle</v-icon>Esegui suite completa
                </v-btn>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <v-row>
              <v-col cols="12" :md="availablePlaybooks.length > 0 ? 5 : 10">
                <v-select
                  v-model="selectedFolders"
                  :items="playwrightFolders"
                  multiple
                  chips
                  clearable
                  dense
                  color="primary"
                  label="Playbooks da eseguire"
                  hint="Seleziona uno o piu playbook da eseguire"
                  persistent-hint
                  :disabled="running || playwrightFolders.length === 0"
                />
              </v-col>
              <v-col cols="12" md="5" v-if="availablePlaybooks.length > 0">
                <v-select
                  v-model="selectedPlaybooks"
                  :items="availablePlaybooks"
                  multiple
                  chips
                  clearable
                  dense
                  color="primary"
                  label="Playbook specifici"
                  hint="Lascia vuoto per eseguire tutti i playbook delle cartelle selezionate"
                  persistent-hint
                  :disabled="running"
                  :loading="loadingPlaybooks"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  block
                  color="primary"
                  :disabled="running || selectedFolders.length === 0"
                  :loading="running"
                  @click="runSelectedPlaybook"
                >
                  <v-icon left>mdi-play</v-icon>Esegui selezione
                </v-btn>
              </v-col>
            </v-row>

            <v-alert v-if="playwrightFolders.length === 0" type="warning" dense text>
              Nessun playbook trovato in playwright-tests/playbooks.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="noMarginCard" v-if="lastRun">
          <v-card-title>Ultima esecuzione</v-card-title>
          <v-card-text>
            <v-chip small :color="lastRun.success ? 'success' : 'error'" dark>
              {{ lastRun.success ? 'SUCCESS' : 'FAILED' }}
            </v-chip>
            <v-btn
              class="ml-3"
              small
              color="secondary"
              @click="openPlaywrightReport"
            >
              <v-icon left>mdi-file-chart</v-icon>Apri report
            </v-btn>
            <v-btn
              v-if="running"
              class="ml-3"
              small
              color="error"
              :loading="stopping"
              :disabled="stopping"
              @click="stopCurrentRun"
            >
              <v-icon left>mdi-stop-circle</v-icon>Stop
            </v-btn>
            <div class="mt-2"><strong>Modalita:</strong> {{ lastRun.mode }}</div>
            <div><strong>Flow:</strong> {{ lastRun.flow || selectedFlow }}</div>
            <div><strong>Target:</strong> {{ lastRun.target || selectedTarget }}</div>
            <div><strong>Comando:</strong> {{ lastRun.command }}</div>
            <div><strong>Exit code:</strong> {{ lastRun.exitCode }}</div>
            <div><strong>Durata:</strong> {{ formatDuration(running ? liveDurationMs : (lastRun && lastRun.durationMs ? lastRun.durationMs : 0)) }}</div>

            <div class="mt-3 segmented-bar-wrapper" :class="{ 'bar-indeterminate': running && progressTotal === 0 }">
              <div class="segmented-bar-track">
                <div class="seg seg-passed" :style="{ width: passedPct + '%' }"></div>
                <div class="seg seg-skipped" :style="{ width: skippedPct + '%' }"></div>
                <div class="seg seg-failed" :style="{ width: failedPct + '%' }"></div>
              </div>
              <div class="segmented-bar-legend">
                <span v-if="running && progressTotal === 0" class="legend-running">In esecuzione...</span>
                <template v-else>
                  <span class="legend-item legend-passed"><span class="dot"></span>{{ progressPassed }} passati</span>
                  <span class="legend-item legend-skipped"><span class="dot"></span>{{ progressSkipped }} saltati</span>
                  <span class="legend-item legend-failed"><span class="dot"></span>{{ progressFailed }} falliti</span>
                  <span v-if="progressTotal > 0" class="legend-total">{{ progressDone }} / {{ progressTotal }}</span>
                </template>
              </div>
            </div>

            <div class="mt-3 output-wrapper">
              <div class="output-header">Output</div>
              <div ref="outputContainer" class="output-container">
                <pre class="output-pre">{{ lastRun.output || '' }}</pre>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'mgmt-playwright',
  data: () => ({
    loadingFolders: false,
    running: false,
    stopping: false,
    playwrightTargets: [],
    selectedTarget: '',
    selectedFlow: 'api',
    playwrightFolders: [],
    selectedFolders: [],
    currentRunId: null,
    availablePlaybooks: [],
    selectedPlaybooks: [],
    loadingPlaybooks: false,
    progressTotal: 0,
    progressDone: 0,
    progressPassed: 0,
    progressSkipped: 0,
    progressFailed: 0,
    lastRun: null,
    runStartTime: null,
    liveDurationMs: 0,
    durationTimer: null,
    batchAbortRequested: false,
  }),
  watch: {
    selectedFolders(newFolders) {
      this.refreshAvailablePlaybooks(newFolders);
    },
    selectedFlow() {
      this.refreshAvailablePlaybooks(this.selectedFolders);
    },
  },
  computed: {
    progressPercent() {
      if (!this.progressTotal || this.progressTotal <= 0) return 0;
      const pct = (this.progressDone / this.progressTotal) * 100;
      if (pct < 0) return 0;
      if (pct > 100) return 100;
      return pct;
    },
    passedPct() {
      if (!this.progressTotal) return 0;
      return Math.min((this.progressPassed / this.progressTotal) * 100, 100);
    },
    skippedPct() {
      if (!this.progressTotal) return 0;
      return Math.min((this.progressSkipped / this.progressTotal) * 100, 100);
    },
    failedPct() {
      if (!this.progressTotal) return 0;
      return Math.min((this.progressFailed / this.progressTotal) * 100, 100);
    },
  },
  methods: {
    formatDuration(ms) {
      const total = Number(ms || 0);
      const sec = Math.floor(total / 1000);
      const min = Math.floor(sec / 60);
      const remSec = sec % 60;
      if (min === 0) return `${remSec}s`;
      return `${min}m ${remSec}s`;
    },
    getPlaywrightReportUrl() {
      const token = localStorage.getItem('mgmtAuthToken') || '';
      const baseUrl = '/API/mgmt/playwright/report';
      if (!token) return baseUrl;
      return `${baseUrl}?authToken=${encodeURIComponent(token)}`;
    },
    openPlaywrightReport() {
      window.open(this.getPlaywrightReportUrl(), '_blank');
    },
    async loadPlaywrightFolders() {
      this.loadingFolders = true;
      try {
        const response = await this.chiamataAxios('GET', '/mgmt/playwright/list', {
          silentError: true,
        });
        if (response && response.data && Array.isArray(response.data.targets)) {
          this.playwrightTargets = response.data.targets;
          if (!this.selectedTarget || !this.playwrightTargets.includes(this.selectedTarget)) {
            this.selectedTarget = this.playwrightTargets[0] || '';
          }
        }
        if (response && response.data && Array.isArray(response.data.folders)) {
          this.playwrightFolders = response.data.folders;
          this.selectedFolders = this.selectedFolders.filter((name) => this.playwrightFolders.includes(name));
        }
      } finally {
        this.loadingFolders = false;
      }
    },
    async runFullSuite() {
      await this.runPlaywright({
        mode: 'full',
        flow: this.selectedFlow,
        target: this.selectedTarget,
      });
    },
    buildFullTargetMatrixPayloads() {
      const orderedTargets = ['my.rlz.it', 'fispt.sport4.life', 'rlz.sport4.life'];
      const availableTargets = orderedTargets.filter((target) => this.playwrightTargets.includes(target));
      const payloads = [];

      ['api', 'ui'].forEach((flow) => {
        availableTargets.forEach((target) => {
          payloads.push({
            mode: 'full',
            flow,
            target,
          });
        });
      });

      return payloads;
    },
    async runFullTargetMatrix() {
      const payloads = this.buildFullTargetMatrixPayloads();
      if (!payloads.length) return;
      const fullTestCommandLabel = 'playwright full test';

      this.running = true;
      this.stopping = false;
      this.batchAbortRequested = false;
      this.currentRunId = null;
      this.progressTotal = 0;
      this.progressDone = 0;
      this.progressPassed = 0;
      this.progressSkipped = 0;
      this.progressFailed = 0;
      this.runStartTime = Date.now();
      this.liveDurationMs = 0;
      this.lastRun = {
        success: false,
        mode: 'full-target-matrix',
        flow: 'api+ui',
        target: payloads.map((payload) => payload.target).join(', '),
        command: fullTestCommandLabel,
        exitCode: null,
        durationMs: 0,
        output: '',
      };

      this.startDurationTimer();

      let aggregateOutput = '';
      let allSuccessful = true;

      try {
        for (let index = 0; index < payloads.length; index += 1) {
          if (this.batchAbortRequested) break;

          const payload = payloads[index];
          const previousPayload = index > 0 ? payloads[index - 1] : null;
          const shouldRefreshAuthToken = !previousPayload || previousPayload.target !== payload.target;
          const runPayload = {
            ...payload,
            refreshAuthToken: shouldRefreshAuthToken,
          };
          const header = `\n===== [${index + 1}/${payloads.length}] ${payload.target} ${String(payload.flow || '').toUpperCase()} =====\n`;
          aggregateOutput += header;
          this.lastRun = {
            ...(this.lastRun || {}),
            mode: 'full-target-matrix',
            flow: `${String(payload.flow || '').toUpperCase()} (${index + 1}/${payloads.length})`,
            target: payload.target,
            command: fullTestCommandLabel,
            output: aggregateOutput,
          };
          this.scrollOutputToEnd();

          const result = await this.executePlaywrightRun(runPayload, {
            baseOutput: aggregateOutput,
            commandLabel: fullTestCommandLabel,
          });
          const runOutput = result && result.output ? String(result.output) : '';
          aggregateOutput += runOutput;
          if (!aggregateOutput.endsWith('\n')) aggregateOutput += '\n';

          allSuccessful = allSuccessful && Boolean(result && result.success);
          this.lastRun = {
            ...(this.lastRun || {}),
            ...(result || {}),
            success: allSuccessful,
            mode: 'full-target-matrix',
            flow: `${String(payload.flow || '').toUpperCase()} (${index + 1}/${payloads.length})`,
            target: payload.target,
            command: fullTestCommandLabel,
            durationMs: this.liveDurationMs || ((this.lastRun && this.lastRun.durationMs) || 0),
            output: aggregateOutput,
          };
          this.updateProgressFromOutput();
          this.scrollOutputToEnd();
        }

        this.lastRun = {
          ...(this.lastRun || {}),
          success: allSuccessful && !this.batchAbortRequested,
          mode: 'full-target-matrix',
          flow: 'api+ui',
          target: payloads.map((payload) => payload.target).join(', '),
          command: fullTestCommandLabel,
          durationMs: this.liveDurationMs || ((this.lastRun && this.lastRun.durationMs) || 0),
          output: aggregateOutput,
        };
      } finally {
        this.stopDurationTimer();
        this.currentRunId = null;
        this.stopping = false;
        this.running = false;
        this.batchAbortRequested = false;
      }
    },
    startDurationTimer() {
      this.stopDurationTimer();
      if (!this.running) return;
      this.runStartTime = Date.now();
      this.liveDurationMs = 0;
      this.durationTimer = setInterval(() => {
        if (!this.running || !this.runStartTime) return;
        this.liveDurationMs = Date.now() - this.runStartTime;
        if (this.lastRun) {
          this.lastRun = {
            ...this.lastRun,
            durationMs: this.liveDurationMs,
          };
        }
      }, 1000);
    },
    stopDurationTimer() {
      if (this.durationTimer) {
        clearInterval(this.durationTimer);
        this.durationTimer = null;
      }
    },
    async loadLastRunFromResults() {
      try {
        const response = await this.chiamataAxios('GET', '/mgmt/playwright/lastRun', {
          silentError: true,
        });
        if (response && response.data && response.data.lastRun) {
          this.lastRun = response.data.lastRun;
          this.updateProgressFromOutput();
          this.scrollOutputToEnd();
        }
      } catch (e) {
        // ignore and keep current state
      }
    },
    refreshAvailablePlaybooks(folders) {
      this.selectedPlaybooks = [];
      this.availablePlaybooks = [];
      if (folders && folders.length > 0) {
        this.loadAvailablePlaybooks(folders);
      }
    },
    async loadAvailablePlaybooks(folders) {
      this.loadingPlaybooks = true;
      try {
        const response = await this.chiamataAxios('POST', '/mgmt/playwright/listPlaybooks', {
          body: { folders, flow: this.selectedFlow },
          silentError: true,
        });
        if (response && response.data && Array.isArray(response.data.playbooks)) {
          this.availablePlaybooks = response.data.playbooks;
        } else {
          this.availablePlaybooks = [];
        }
      } finally {
        this.loadingPlaybooks = false;
      }
    },
    async runSelectedPlaybook() {
      const payload = {
        mode: 'playbook',
        flow: this.selectedFlow,
        folders: this.selectedFolders,
        target: this.selectedTarget,
      };
      if (this.selectedPlaybooks.length > 0) {
        payload.playbooks = this.selectedPlaybooks;
      }
      await this.runPlaywright(payload);
    },
    async runPlaywright(payload) {
      this.running = true;
      this.stopping = false;
      this.batchAbortRequested = false;
      this.currentRunId = null;
      this.progressTotal = 0;
      this.progressDone = 0;
      this.runStartTime = Date.now();
      this.liveDurationMs = 0;
      this.lastRun = {
        success: false,
        mode: payload && payload.mode ? payload.mode : 'full',
        flow: payload && payload.flow ? payload.flow : this.selectedFlow,
        target: payload && payload.target ? payload.target : this.selectedTarget,
        command: 'starting...',
        exitCode: null,
        durationMs: 0,
        output: '',
      };

      this.startDurationTimer();

      try {
        const result = await this.executePlaywrightRun(payload);
        this.lastRun = {
          ...(this.lastRun || {}),
          ...(result || {}),
          durationMs: Number(
            (result && typeof result.durationMs !== 'undefined' && result.durationMs !== null)
              ? result.durationMs
              : (this.liveDurationMs || ((this.lastRun && this.lastRun.durationMs) || 0))
          ),
        };
        this.updateProgressFromOutput();
        this.scrollOutputToEnd();
      } finally {
        this.stopDurationTimer();
        this.currentRunId = null;
        this.stopping = false;
        this.running = false;
      }
    },
    async stopCurrentRun() {
      this.batchAbortRequested = true;
      if (!this.currentRunId || this.stopping) return;
      this.stopping = true;
      try {
        await this.chiamataAxios('POST', '/mgmt/playwright/stop', {
          body: { runId: this.currentRunId },
          silentError: true,
          returnDataOnError: true,
        });
      } finally {
        this.stopping = false;
      }
    },
    async executePlaywrightRun(payload, options = {}) {
      const streamResult = await this.runPlaywrightStream(payload, options);
      if (streamResult) {
        this.currentRunId = null;
        return streamResult;
      }

      const response = await this.chiamataAxios('POST', '/mgmt/playwright/run', {
        body: payload,
        secTimeout: 7200,
        dialogLoader: true,
        silentError: true,
        returnDataOnError: true,
      });
      return response && response.data ? response.data : null;
    },
    async runPlaywrightStream(payload, options = {}) {
      const token = localStorage.getItem('mgmtAuthToken') || '';
      const response = await fetch(`/API/mgmt/playwright/run?stream=true&t=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          local: window.selectedLanguage || 'it',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(payload || {}),
      });

      if (!response.ok || !response.body) {
        return null;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/event-stream')) {
        return null;
      }

      const decoder = new TextDecoder();
      const reader = response.body.getReader();
      let buffer = '';
      let finalResult = null;
      const manageUi = options.manageUi !== false;
      const baseOutput = typeof options.baseOutput === 'string' ? options.baseOutput : '';
      const commandLabel = typeof options.commandLabel === 'string' ? options.commandLabel : '';
      let streamedOutput = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || '';

        for (const rawMessage of messages) {
          if (!rawMessage || rawMessage.trim() === '') continue;
          const parsed = this.parseSseMessage(rawMessage);
          if (!parsed) continue;

          const data = this.safeJsonParse(parsed.data);
          if (parsed.event === 'meta' && data) {
            if (data.runId) {
              this.currentRunId = String(data.runId);
            }
            if (manageUi) {
              this.lastRun = {
                ...(this.lastRun || {}),
                mode: data.mode || this.lastRun.mode,
                flow: data.flow || this.lastRun.flow,
                target: data.target || this.lastRun.target,
                command: commandLabel || data.command || this.lastRun.command,
                output: baseOutput + streamedOutput,
              };
            }
            continue;
          }

          if (parsed.event === 'output' && data && typeof data.chunk === 'string') {
            streamedOutput += data.chunk;
            if (manageUi) {
              this.lastRun = {
                ...(this.lastRun || {}),
                output: baseOutput + streamedOutput,
              };
              this.updateProgressFromOutput();
              this.scrollOutputToEnd();
            }
            continue;
          }

          if (parsed.event === 'error' && data && data.message) {
            streamedOutput += `${streamedOutput.endsWith('\n') || streamedOutput === '' ? '' : '\n'}${data.message}`;
            if (manageUi) {
              this.lastRun = {
                ...(this.lastRun || {}),
                output: baseOutput + streamedOutput,
              };
              this.updateProgressFromOutput();
              this.scrollOutputToEnd();
            }
            continue;
          }

          if (parsed.event === 'end' && data) {
            finalResult = data;
          }
        }
      }

      return finalResult;
    },
    parseSseMessage(rawMessage) {
      const lines = String(rawMessage)
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '');

      if (!lines.length) return null;

      let eventName = 'message';
      const dataLines = [];

      lines.forEach((line) => {
        if (line.startsWith('event:')) {
          eventName = line.substring(6).trim();
          return;
        }
        if (line.startsWith('data:')) {
          dataLines.push(line.substring(5).trim());
        }
      });

      return {
        event: eventName,
        data: dataLines.join('\n'),
      };
    },
    safeJsonParse(value) {
      try {
        return JSON.parse(value);
      } catch (e) {
        return null;
      }
    },
    updateProgressFromOutput() {
      const output = this.lastRun && this.lastRun.output ? String(this.lastRun.output) : '';
      if (!output) {
        this.progressTotal = 0;
        this.progressDone = 0;
        this.progressPassed = 0;
        this.progressSkipped = 0;
        this.progressFailed = 0;
        return;
      }

      const runningMatches = Array.from(output.matchAll(/Running\s+(\d+)\s+tests?/gi));
      const computedTotal = runningMatches.reduce((acc, match) => {
        const value = Number(match && match[1]);
        return Number.isFinite(value) && value > 0 ? acc + value : acc;
      }, 0);
      this.progressTotal = computedTotal > 0 ? computedTotal : 0;

      // Aggregate multiple run summaries (full matrix) and keep live inline counters for the current running step.
      const passedSummaryMatches = Array.from(output.matchAll(/(\d+)\s+passed/gi));
      const failedSummaryMatches = Array.from(output.matchAll(/(\d+)\s+failed/gi));
      const skippedSummaryMatches = Array.from(output.matchAll(/(\d+)\s+skipped/gi));

      const passedSummary = passedSummaryMatches.reduce((acc, match) => acc + (Number(match && match[1]) || 0), 0);
      const failedSummary = failedSummaryMatches.reduce((acc, match) => acc + (Number(match && match[1]) || 0), 0);
      const skippedSummary = skippedSummaryMatches.reduce((acc, match) => acc + (Number(match && match[1]) || 0), 0);

      const passedLines = output.match(/^\s*✓\s+\d+\s+\[/gm);
      const failedLines = output.match(/^\s*(?:✘|×)\s+\d+\s+\[/gm);
      const skippedLines = output.match(/^\s*-\s+\d+\s+\[/gm);
      const passedInline = passedLines ? passedLines.length : 0;
      const failedInline = failedLines ? failedLines.length : 0;
      const skippedInline = skippedLines ? skippedLines.length : 0;

      this.progressPassed = Math.max(passedSummary, passedInline);
      this.progressFailed = Math.max(failedSummary, failedInline);
      this.progressSkipped = Math.max(skippedSummary, skippedInline);

      const done = this.progressPassed + this.progressSkipped + this.progressFailed;
      this.progressDone = this.progressTotal > 0 ? Math.min(done, this.progressTotal) : done;
    },
    scrollOutputToEnd() {
      this.$nextTick(() => {
        const el = this.$refs.outputContainer;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
      });
    },
  },
  mounted() {
    this.loadPlaywrightFolders();
    this.loadLastRunFromResults();
  },
};
</script>

<style scoped>
.playwright-flow-switch >>> .v-input--switch__track {
  background-color: rgba(76, 175, 80, 0.38) !important;
  border-color: rgba(76, 175, 80, 0.38) !important;
}

.playwright-flow-switch >>> .v-input--switch__thumb {
  background-color: #4caf50 !important;
}

.playwright-flow-switch >>> .v-input--switch.v-input--is-checked .v-input--switch__track {
  background-color: #4caf50 !important;
  border-color: #4caf50 !important;
}

.playwright-flow-switch >>> .v-input--switch.v-input--is-checked .v-input--switch__thumb {
  background-color: #4caf50 !important;
}

.playwright-flow-switch >>> .v-input--selection-controls__label {
  color: #4caf50 !important;
}

.playwright-flow-switch >>> .v-input--selection-controls__ripple,
.playwright-flow-switch >>> .v-input--selection-controls__input {
  color: #4caf50 !important;
}

.playwright-flow-switch >>> .v-input--selection-controls__prepend-icon,
.playwright-flow-switch >>> .v-input--selection-controls__append-icon {
  color: #4caf50 !important;
}

.playwright-flow-switch >>> .v-icon {
  color: #4caf50 !important;
}

.playwright-flow-switch.ui-active >>> .v-input--switch__track,
.playwright-flow-switch.ui-active >>> .v-input--switch__thumb,
.playwright-flow-switch.ui-active >>> .v-input--selection-controls__label,
.playwright-flow-switch.ui-active >>> .v-input--selection-controls__prepend-icon,
.playwright-flow-switch.ui-active >>> .v-input--selection-controls__append-icon,
.playwright-flow-switch.ui-active >>> .v-icon {
  color: var(--v-primary-base, #1976d2) !important;
}

.playwright-flow-switch.ui-active >>> .v-input--switch__track {
  background-color: rgba(25, 118, 210, 0.38) !important;
  border-color: rgba(25, 118, 210, 0.38) !important;
}

.playwright-flow-switch.ui-active >>> .v-input--switch__thumb {
  background-color: var(--v-primary-base, #1976d2) !important;
}

.playwright-flow-switch.ui-active >>> .v-input--switch.v-input--is-checked .v-input--switch__track {
  background-color: var(--v-primary-base, #1976d2) !important;
  border-color: var(--v-primary-base, #1976d2) !important;
}

.playwright-flow-switch.ui-active >>> .v-input--switch.v-input--is-checked .v-input--switch__thumb {
  background-color: var(--v-primary-base, #1976d2) !important;
}

.output-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.16);
  border-radius: 4px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 720px);
  height: calc(100vh - 720px);
  max-height: calc(100vh - 720px);
}

.output-header {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.output-container {
  flex: 1 1 auto;
  min-height: 85%;
  overflow-y: auto;
  padding: 10px 12px;
}

.output-pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.35;
}

.segmented-bar-wrapper {
  user-select: none;
}

.segmented-bar-track {
  display: flex;
  height: 18px;
  border-radius: 4px;
  overflow: hidden;
  background: #e0e0e0;
}

.seg {
  height: 100%;
  transition: width 0.3s ease;
}

.seg-passed  { background: #4caf50; }
.seg-skipped { background: #29b6f6; }
.seg-failed  { background: #f44336; }

.bar-indeterminate .segmented-bar-track {
  background: linear-gradient(90deg, #e0e0e0 25%, #bdbdbd 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: bar-pulse 1.4s infinite linear;
}

@keyframes bar-pulse {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.segmented-bar-legend {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 5px;
  font-size: 12px;
  flex-wrap: wrap;
}

.legend-item { display: flex; align-items: center; gap: 4px; }

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-passed .dot  { background: #4caf50; }
.legend-skipped .dot { background: #29b6f6; }
.legend-failed .dot  { background: #f44336; }

.legend-total   { color: rgba(0,0,0,0.5); margin-left: auto; }
.legend-running { color: rgba(0,0,0,0.6); font-style: italic; }
</style>
